import { useEffect, useState } from "react";

import { Alert, CircularProgress, Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DashboardBackButton } from "components/Shipment/components/DashboardBackButton/DashboardBackButton";
import { ClientAppEvent, ShipmentMicroReviewSource } from "constants/index";
import { useMicroReviewSubmission } from "hooks";
import { PageLayout } from "layouts";

const MICRO_REVIEW_TOKEN_KEY = "microReviewToken";
const REDIRECT_DELAY_SECONDS = 5;

export function MicroReviewContainer() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [countdown, setCountdown] = useState<number>(REDIRECT_DELAY_SECONDS);

  // Capture token from URL or sessionStorage, store in sessionStorage for persistence across unmounts
  const token =
    searchParams.get("token") ?? sessionStorage.getItem(MICRO_REVIEW_TOKEN_KEY);

  useEffect(() => {
    if (token) {
      sessionStorage.setItem(MICRO_REVIEW_TOKEN_KEY, token);
    }
  }, [token]);

  const { status, errorMessage } = useMicroReviewSubmission(token, () => {
    posthog?.capture(ClientAppEvent.SHIPMENT_MICRO_REVIEW_SUBMITTED, {
      source: ShipmentMicroReviewSource.EMAIL_LINK,
    });
  });

  const handleBeforeNavigate = () => {
    // Clear token before navigating
    sessionStorage.removeItem(MICRO_REVIEW_TOKEN_KEY);
  };

  useEffect(() => {
    // 1. Exit early if timer hits 0 to trigger the redirect
    if (countdown <= 0) {
      handleBeforeNavigate();
      navigate("/");
      return undefined;
    }

    // 2. Set up the interval timer
    const timerId = setInterval(() => {
      if (status !== "loading") {
        setCountdown((prev) => prev - 1);
      }
    }, 1000);

    // 3. Clean up the interval on unmount or before running again
    return () => {
      clearInterval(timerId);
    };
  }, [countdown, navigate, status]);

  if (status === "loading") {
    return (
      <Stack alignItems="center" spacing={2}>
        <Typography textAlign="center" variant="h6">
          Submitting your review...
        </Typography>
        <CircularProgress />
      </Stack>
    );
  }

  if (status === "error") {
    return (
      <PageLayout>
        <Stack alignItems="center" spacing={2}>
          <Alert severity="error">
            <Typography variant="h6">Review Submission Failed</Typography>
            <Typography>{errorMessage}</Typography>
            {countdown > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Redirecting to dashboard in {countdown} seconds...
              </Typography>
            )}
          </Alert>
          <DashboardBackButton onBeforeNavigate={handleBeforeNavigate} />
        </Stack>
      </PageLayout>
    );
  }

  if (status === "success") {
    return (
      <PageLayout>
        <Stack alignItems="center" spacing={2}>
          <Alert severity="success">
            <Typography variant="h6">Thank you for your review!</Typography>
            <Typography>
              Your response has been registered anonymously. Your feedback helps
              improve our platform.
            </Typography>
            {countdown > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Redirecting to dashboard in {countdown} seconds...
              </Typography>
            )}
          </Alert>
          <DashboardBackButton onBeforeNavigate={handleBeforeNavigate} />
        </Stack>
      </PageLayout>
    );
  }

  return null;
}
